import { AutoComplete, AutoCompleteCompleteEvent, AutoCompleteModule } from 'primeng/autocomplete';

import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  forwardRef,
  inject,
  input,
  model,
  OnDestroy,
  Provider,
  signal,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import {
  CONTRIBUTION_TYPE,
  DynamicTranslationService,
  GlobalConfigApiService,
  NumberFormatConfig,
} from '@gs-2.0/core';

import { IconComponent } from '../icon/icon.component';
import { AmountCurrencyValue, CurrencySet } from './amount-currency-input.model';
import { TranslateLangPipe } from '../../pipes/translate-lang.pipe';

/**
 * Provider for the ControlValueAccessor to support Reactive Forms.
 */
const AMOUNT_CURRENCY_VALUE_ACCESSOR: Provider = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => AmountCurrencyInputComponent),
  multi: true,
};

/**
 * Provider for the Validator to support built-in form validation.
 */
const AMOUNT_CURRENCY_VALIDATOR: Provider = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => AmountCurrencyInputComponent),
  multi: true,
};

@Component({
  selector: 'app-amount-currency-input',
  imports: [AutoCompleteModule, FormsModule, IconComponent, TranslateLangPipe],
  providers: [AMOUNT_CURRENCY_VALUE_ACCESSOR, AMOUNT_CURRENCY_VALIDATOR],
  templateUrl: './amount-currency-input.html',
  styleUrl: './amount-currency-input.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AmountCurrencyInputComponent implements ControlValueAccessor, Validator, OnDestroy {
  private readonly translationService = inject(DynamicTranslationService);
  private readonly globalConfigApiService = inject(GlobalConfigApiService);

  @ViewChild('currencyAutoComplete') private readonly currencyAutoComplete!: AutoComplete;

  // --- Inputs & Models ---
  /** Two-way bindable amount. */
  readonly amount = model<number | null>(null);

  /** Two-way bindable currency. */
  readonly currency = model<CurrencySet | null>(null);

  /** List of available currencies to choose from. */
  readonly currencySets = input<CurrencySet[]>([]);

  /** Minimum amount required. */
  readonly minimumAmount = input<number | null>(null);

  /** Signal reflecting the disabled state. */
  readonly disabled = signal(false);

  /** Whether only the currency input should be disabled. */
  readonly currencyDisabled = input(false);

  /**
   * When true, replaces the clear button with a persistent dropdown arrow indicator.
   * Opt-in only — do not enable on pages other than the Make a Contribution section.
   */
  readonly selectionArrow = input(false);

  /** Label for the amount field. */
  readonly label = input('contribute.section4.amount.label');

  /** Whether the field is required. */
  readonly required = input(false);

  /** Filter currencies by tax deduction status. When null, no filtering is applied. */
  readonly taxDeduction = input<string | null>(null);

  /** Filter currencies by contribution type. When null, no filtering is applied. */
  readonly contributionType = input<string | null>(null);

  /** Additional filter check for tax deductible country */
  readonly isTaxDeductionAvailable = input<boolean>(false);

  /** Whether the field has a validation error (from parent). */
  readonly hasError = input<boolean>(false);

  /**
   * Overrides the default minimum-amount error text. Used where the minimum needs its own
   * explanation (e.g. the South Africa / ZAR bank-transfer rule on the donate form). Pass an
   * already-translated string; leave it empty to keep the generic message. Other errors
   * (required amount/currency, invalid currency) always keep their own text.
   */
  readonly errorMessage = input<string>('');

  /** Number format configuration loaded from global config API. */
  readonly numberFormat = signal<NumberFormatConfig>({
    decimalSymbol: '.',
    digitGroupingSymbol: ',',
    digitGrouping: '123,456,789',
  });

  /**
   * Display value for the amount input.
   * Updated directly during typing (without forcing decimals) and formatted on blur.
   */
  protected readonly displayAmount = signal('');

  // --- Internal States ---
  /** Filtered items for the autocomplete dropdown. */
  private readonly filteredItems = signal<CurrencySet[]>([]);

  /** Dropdown items - shows filtered results or all currencies. */
  protected readonly dropDownItems = computed(() => {
    const filtered = this.filteredItems();
    return filtered.length > 0 ? filtered : this.currencySets();
  });

  /** Sets a large scroll height to remove the scrollbar when fewer than 6 items are shown. */
  protected readonly currencyScrollHeight = computed(() =>
    this.dropDownItems().length < 6 ? '1000px' : '200px'
  );

  /** Current placeholder text for the currency input. */
  protected readonly placeholder = signal('contribute.section4.currency.placeholder');

  /** Whether the component has been interacted with. */
  protected readonly isTouched = signal(false);

  /** Tracks whether the currency dropdown panel is open. */
  protected readonly isDropdownOpen = signal(false);

  /** True when the user typed a string that doesn't match any available currency. */
  protected readonly hasInvalidCurrencyInput = signal(false);

  /** Whether to show the persistent dropdown arrow (opt-in via selectionArrow input). */
  protected readonly showDropdownArrow = computed(
    () => this.selectionArrow() && !this.disabled() && !this.currencyDisabled() && this.currencySets().length > 1
  );

  /** Timer for debouncing real-time validation. */
  private validationDebounceTimer: any = null;

  /** Flag to track if this is the first effect run (initialization vs user interaction). */
  private isFirstEffectRun = true;

  // --- Form Callbacks ---
  private onChange: (value: AmountCurrencyValue) => void = () => {};
  private onTouched: () => void = () => {};
  private onValidatorChange: () => void = () => {};

  // --- Computed Properties ---
  /** Derived error message for UI display. */
  protected readonly displayErrorMessage = computed(() => {
    const errors = this.validateInternal();
    const isActuallyTouched = this.isTouched() || this.hasError();

    // Prioritize internal granular validation for required/min fields
    if (isActuallyTouched && errors) {
      const missing = [];
      if (errors['requiredAmount'])
        missing.push(
          this.translationService.instant('shared.amount_currency_input.error.an_amount')
        );
      if (errors['requiredCurrency'])
        missing.push(
          this.translationService.instant('shared.amount_currency_input.error.a_currency')
        );

      if (missing.length > 0) {
        const andSeparator = this.translationService.instant('common.and');
        return this.translationService.instant('shared.amount_currency_input.error.please_enter', {
          missing: missing.join(` ${andSeparator} `),
        });
      }

      if (errors['minAmount']) {
        return (
          this.errorMessage() ||
          this.translationService.instant('shared.amount_currency_input.error.min_amount', {
            min: this.formatMinimumAmount(),
          })
        );
      }

      if (errors['invalidCurrency']) {
        return this.translationService.instant('shared.amount_currency_input.error.invalid_currency');
      }
    }

    return null;
  });

  /** Whether the field is currently in an invalid state. */
  protected readonly isInvalid = computed(() => this.hasError() || !!this.displayErrorMessage());

  constructor() {
    this.loadGlobalConfig();

    // Clear invalid-input flag whenever a valid CurrencySet object is selected
    // and re-trigger validation so the parent form control error is removed.
    effect(() => {
      const c = this.currency();
      if (c != null && typeof c === 'object' && 'symbol' in c) {
        this.hasInvalidCurrencyInput.set(false);
        queueMicrotask(() => {
          this.onChange({ amount: this.amount(), currency: c });
        });
      }
    });

    // Sync model changes back to the Form Control / NgModel
    effect(() => {
      const value = {
        amount: this.amount(),
        currency: this.currency(),
      };

      // Using queueMicrotask is more efficient than setTimeout.
      // It schedules the update after the current task is done,
      // preventing ExpressionChangedAfterItHasBeenCheckedError.
      queueMicrotask(() => {
        this.onChange(value);
      });

      // Skip debounced validation on first run (component initialization)
      if (this.isFirstEffectRun) {
        this.isFirstEffectRun = false;
        return;
      }

      // Real-time validation with 300ms debounce (only for user interactions)
      if (this.validationDebounceTimer) {
        clearTimeout(this.validationDebounceTimer);
      }

      this.validationDebounceTimer = setTimeout(() => {
        // Mark as touched to enable error display
        // Only if there's actual input (amount or currency is not null)
        if (value.amount !== null || value.currency !== null) {
          this.isTouched.set(true);
        }
      }, 300);
    });

    // Angular only re-runs a Validator when the control VALUE changes, so a new minimumAmount
    // (e.g. the donate form switching currency off ZAR) would otherwise leave a stale minAmount
    // error on the parent control. queueMicrotask matches the effects above and keeps the
    // notification out of the current change-detection pass.
    effect(() => {
      this.minimumAmount();
      queueMicrotask(() => this.onValidatorChange());
    });
  }

  private loadGlobalConfig(): void {
    this.globalConfigApiService.getConfiguration().subscribe({
      next: (config: any) => {
        if (config?.response?.numberFormat) {
          this.numberFormat.set({
            decimalSymbol: config.response.numberFormat.decimalSymbol?.value || '.',
            digitGroupingSymbol: config.response.numberFormat.digitGroupingSymbol?.value || ',',
            digitGrouping: config.response.numberFormat.digitGrouping || '123,456,789',
          });
        }
      },
    });
  }

  // --- ControlValueAccessor Implementation ---

  writeValue(value: AmountCurrencyValue | null): void {
    // CRITICAL FIX: Ignore spurious calls with null currency when we already have a valid one.
    // This prevents form sync logic from clearing the currency value during navigation.
    if (!value?.currency && this.currency() && value?.amount === this.amount()) {
      return;
    }

    const amount = value?.amount ?? null;
    this.amount.set(amount);
    this.currency.set(value?.currency ?? null);
    this.displayAmount.set(amount !== null && amount !== undefined ? this.formatNumberValue(amount) : '');
  }

  registerOnChange(fn: (value: AmountCurrencyValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  // --- Validator Implementation ---

  validate(control: AbstractControl): ValidationErrors | null {
    return this.validateInternal(control.value);
  }

  /**
   * Internal logic to check for validation errors.
   */
  private validateInternal(value?: AmountCurrencyValue): ValidationErrors | null {
    const val = value ?? { amount: this.amount(), currency: this.currency() };
    const errors: ValidationErrors = {};

    if (this.required()) {
      if (val.amount === null || val.amount === undefined) {
        errors['requiredAmount'] = true;
      }
      if (!val.currency) {
        errors['requiredCurrency'] = true;
      }
    }

    if (
      this.minimumAmount() !== null &&
      val.amount !== null &&
      val.amount !== undefined &&
      val.amount < this.minimumAmount()!
    ) {
      errors['minAmount'] = {
        min: this.minimumAmount(),
        actual: val.amount,
      };
    }

    if (this.hasInvalidCurrencyInput()) {
      errors['invalidCurrency'] = true;
    }

    return Object.keys(errors).length > 0 ? errors : null;
  }

  /**
   * Formats the minimum amount with thousands separator and currency code suffix for BRL.
   * Uses global config number format (same approach as activity page).
   */
  private formatMinimumAmount(): string {
    const min = this.minimumAmount();
    if (min === null || min === undefined) {
      return '';
    }

    const formattedAmount = this.formatNumberValue(min);

    // Get current currency
    const currentCurrency = this.currency();

    // For BRL currency, append " BRL" after the formatted amount
    if (currentCurrency?.symbol === 'BRL') {
      return `${formattedAmount} BRL`;
    }

    return formattedAmount;
  }

  /**
   * Formats a number using global config number format values directly.
   * Same pattern as the activity page's formatNumberValue.
   */
  private formatNumberValue(value: number): string {
    const fmt = this.numberFormat();
    const parts = Math.abs(value).toFixed(2).split('.');
    const intPart = parts[0];
    const decPart = parts[1];
    const grouped = this.applyDigitGrouping(intPart, fmt.digitGroupingSymbol, fmt.digitGrouping);
    const result = grouped + fmt.decimalSymbol + decPart;
    return value < 0 ? `-${result}` : result;
  }

  private applyDigitGrouping(intPart: string, groupSymbol: string, groupPattern: string): string {
    const patternParts = groupPattern.replace(/[^0-9,]/g, '').split(',');
    if (patternParts.length <= 1) return intPart;

    const groupSizes = patternParts.map((p) => p.length).reverse();
    const digits = intPart.split('').reverse();
    const groups: string[] = [];
    let pos = 0;
    let sizeIdx = 0;

    while (pos < digits.length) {
      const size = groupSizes[Math.min(sizeIdx, groupSizes.length - 1)];
      groups.push(digits.slice(pos, pos + size).reverse().join(''));
      pos += size;
      sizeIdx++;
    }

    return groups.reverse().join(groupSymbol);
  }

  // --- Amount Input Event Handlers ---

  /**
   * Parses a formatted string back to a number using the global config symbols.
   */
  private parseFormattedValue(formatted: string): number | null {
    if (!formatted || formatted.trim() === '') return null;
    const fmt = this.numberFormat();
    // Remove grouping symbols, then replace decimal symbol with '.'
    const escaped = fmt.digitGroupingSymbol.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const cleaned = formatted.replace(new RegExp(escaped, 'g'), '');
    const normalized = cleaned.replace(fmt.decimalSymbol, '.');
    const parsed = parseFloat(normalized);
    return isNaN(parsed) ? null : parsed;
  }

  /** Handles keydown to allow only valid characters for the configured number format. */
  protected onAmountKeyDown(event: KeyboardEvent) {
    const fmt = this.numberFormat();
    const allowedKeys = [
      'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End',
    ];
    if (allowedKeys.includes(event.key)) return;
    if ((event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x', 'z'].includes(event.key.toLowerCase())) return;

    // Allow digits
    if (/^\d$/.test(event.key)) return;

    // Allow the configured decimal symbol (only once)
    if (event.key === fmt.decimalSymbol) {
      const target = event.target as HTMLInputElement;
      if (target.value.includes(fmt.decimalSymbol)) {
        event.preventDefault();
      }
      return;
    }

    // Block everything else
    event.preventDefault();
  }

  /** Handles input - formats as you type with digit grouping and max 2 decimal places. */
  protected onAmountInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const fmt = this.numberFormat();
    const cursorPos = input.selectionStart || 0;
    const oldValue = input.value;

    // Strip grouping symbols to get raw digits + decimal
    const escapedGrouping = fmt.digitGroupingSymbol.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    let raw = oldValue.replace(new RegExp(escapedGrouping, 'g'), '');

    // Split on decimal symbol
    const decIdx = raw.indexOf(fmt.decimalSymbol);
    let intRaw: string;
    let decRaw: string | undefined;

    if (decIdx >= 0) {
      intRaw = raw.substring(0, decIdx).replace(/\D/g, '');
      decRaw = raw.substring(decIdx + 1).replace(/\D/g, '').substring(0, 2);
    } else {
      intRaw = raw.replace(/\D/g, '');
    }

    // Remove leading zeros (keep at least one digit)
    intRaw = intRaw.replace(/^0+(?=\d)/, '');

    // Apply digit grouping to integer part
    const grouped = intRaw ? this.applyDigitGrouping(intRaw, fmt.digitGroupingSymbol, fmt.digitGrouping) : '';

    // Build formatted value
    let formatted: string;
    if (decIdx >= 0) {
      formatted = grouped + fmt.decimalSymbol + (decRaw ?? '');
    } else {
      formatted = grouped;
    }

    // Count non-grouping chars before cursor in old value
    let digitsBefore = 0;
    for (let i = 0; i < cursorPos && i < oldValue.length; i++) {
      if (oldValue[i] !== fmt.digitGroupingSymbol) digitsBefore++;
    }

    // Find new cursor position
    let newPos = 0;
    let count = 0;
    for (let i = 0; i < formatted.length; i++) {
      if (formatted[i] !== fmt.digitGroupingSymbol) count++;
      if (count === digitsBefore) {
        newPos = i + 1;
        break;
      }
    }
    if (count < digitsBefore) newPos = formatted.length;

    input.value = formatted;
    input.setSelectionRange(newPos, newPos);
    this.displayAmount.set(formatted);

    // Parse and update model
    const parsed = this.parseFormattedValue(formatted);
    this.amount.set(parsed);
  }

  /** On blur, format the displayed value with full decimal places and mark as touched. */
  protected onAmountBlur(event: Event) {
    const input = event.target as HTMLInputElement;
    const parsed = this.parseFormattedValue(input.value);
    this.amount.set(parsed);
    // Reformat with full 2 decimal places
    if (parsed !== null) {
      const formatted = this.formatNumberValue(parsed);
      this.displayAmount.set(formatted);
      input.value = formatted;
    } else if (input.value.trim() === '') {
      this.displayAmount.set('');
      input.value = '';
    }
    this.isTouched.set(true);
    this.onTouched();
  }

  // --- UI Event Handlers ---

  /** Marks the component as touched and validates the raw currency input text. */
  protected handleBlur() {
    this.isTouched.set(true);
    this.onTouched();

    const text = (this.currencyAutoComplete?.inputEL?.nativeElement?.value ?? '').trim();
    const currentCurrency = this.currency();
    const hasValidSelection =
      currentCurrency != null &&
      typeof currentCurrency === 'object' &&
      'symbol' in currentCurrency;

    this.hasInvalidCurrencyInput.set(text.length > 0 && !hasValidSelection);

    // Angular only calls validate() on value changes. Force a re-validation cycle
    // so the parent form control picks up the invalidCurrency error set above.
    queueMicrotask(() => {
      this.onChange({ amount: this.amount(), currency: this.currency() });
    });
  }

  /** Opens the currency dropdown panel when the arrow icon is clicked. */
  protected onArrowClick() {
    this.currencyAutoComplete?.show();
    this.currencyAutoComplete?.inputEL?.nativeElement?.focus();
  }

  /** Removes placeholder when dropdown appears (improves PrimeNG visual consistency). */
  protected onCurrencyShow() {
    this.placeholder.set('');
    this.isDropdownOpen.set(true);
  }

  /** Restores placeholder when dropdown is hidden. */
  protected onCurrencyHide() {
    this.placeholder.set('contribute.section4.currency.placeholder');
    this.isDropdownOpen.set(false);
  }

  /**
   * Filters suggestions based on user input and specific business criteria.
   * Specific to CREDIT_CARD and non-tax deductible currencies.
   */
  protected filter(event: AutoCompleteCompleteEvent) {
    const query = (event.query || '').toLowerCase();
    const selectedSymbol = this.currency()?.symbol?.toLowerCase() ?? '';
    const taxDeduction = this.taxDeduction();
    const contributionType = this.contributionType();

    const checkTaxDeduction = (c: CurrencySet) =>
      taxDeduction === null ||
      c.taxDeduction === (taxDeduction ? 'TRUE' : this.isTaxDeductionAvailable() ? 'FALSE' : 'NA');

    const checkContributionType = (c: CurrencySet) => {
      if (!contributionType) return true;

      switch (contributionType) {
        case CONTRIBUTION_TYPE.BANK_TRANSFER:
        case CONTRIBUTION_TYPE.BANK_TRANSFER_OFFLINE:
        case CONTRIBUTION_TYPE.BANK_TRANSFER_ONLINE:
        case CONTRIBUTION_TYPE.DONOR_ADVISED_FUND:
        case CONTRIBUTION_TYPE.OTHER_ASSETS:
        case CONTRIBUTION_TYPE.PHYSICAL_CHECK:
        case CONTRIBUTION_TYPE.PUBLICLY_TRADED_SECURITIES:
          return [
            CONTRIBUTION_TYPE.BANK_TRANSFER,
            CONTRIBUTION_TYPE.BANK_TRANSFER_OFFLINE,
          ].includes(c.contributionType as CONTRIBUTION_TYPE);
        default:
          return c.contributionType === contributionType;
      }
    };

    // Show all eligible options when the query is empty or exactly matches the
    // already-selected currency (i.e. the user focused without typing anything new).
    const showAll = !query || query === selectedSymbol;

    this.filteredItems.set(
      this.currencySets().filter(
        (c) =>
          (showAll || c.symbol.toLowerCase().includes(query)) &&
          checkTaxDeduction(c) &&
          checkContributionType(c)
      )
    );
  }

  /** Clears the selected currency. */
  protected clearCurrency() {
    this.currency.set(null);
  }

  // --- Lifecycle Hooks ---

  ngOnDestroy(): void {
    if (this.validationDebounceTimer) {
      clearTimeout(this.validationDebounceTimer);
      this.validationDebounceTimer = null;
    }
  }
}
